import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, query, where, getDocs } from "firebase/firestore";
import Navigation from "./Navigation";
import "./HomePage.css";

const REVIEWS_PER_PAGE = 5;
const PREVIEW_MAX_HEIGHT = 7.5; // em units (approx 5 lines at 1.5 line-height)

function HomePage() {
  const [reviews, setReviews] = useState([]);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchApprovedReviews = async () => {
      try {
        const q = query(collection(db, "reviews"), where("approved", "==", true));
        const snapshot = await getDocs(q);
        const fetched = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setReviews(fetched);
      } catch (err) {
        console.error("Error fetching reviews:", err);
      }
    };
    fetchApprovedReviews();
  }, []);

  const toggleExpand = (id) => {
    setExpandedReviews((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Pagination
  const totalPages = Math.max(1, Math.ceil(reviews.length / REVIEWS_PER_PAGE));
  const startIndex = (currentPage - 1) * REVIEWS_PER_PAGE;
  const currentReviews = reviews.slice(startIndex, startIndex + REVIEWS_PER_PAGE);

  return (
    <>
      <Navigation />

      <div className="home-container">
        <h1 className="page-title">Customer Reviews</h1>

        <div className="intro-box">
          <p>
            <strong>NRN-Review</strong> has been established to give NRN customers a platform to share
            their experience with the public and also to NRN. NRN-Review is not associated with the
            National Renewables Network (NRN); all opinions and reviews are from genuine NRN customers
            who have been verified as such by supplying an energy bill.
          </p>
        </div>

        <div className="reviews-wrapper">
          {currentReviews.length === 0 && (
            <p style={{ textAlign: "center", color: "#666" }}>No approved reviews yet.</p>
          )}

          {currentReviews.map((review) => {
            const fullText = review.reviewText || "";
            // Decide whether to show a toggle: use newline count OR character length
            const newlineCount = fullText.split("\n").length;
            const needsToggle = newlineCount > 5 || fullText.length > 400;
            const isExpanded = !!expandedReviews[review.id];

            const suburbState = (() => {
              // Use `suburb` and optional `state` fields if available
              if (review.suburb && review.state) return `${review.suburb} - ${review.state}`;
              if (review.suburb) return review.suburb;
              if (review.state) return review.state;
              return "N/A";
            })();

            return (
              <div className="review-card" key={review.id}>
                <p className="review-header">
                  <strong>{review.firstName || "N/A"}</strong> — {suburbState}
                </p>

                <p className="question-line">
                  Have your energy bill savings been in line with NRN’s projections?{" "}
                  <strong>A: {review.q1 || "N/A"}</strong>
                </p>

                <p className="question-line">
                  Would you recommend the NRN Solar + Battery package to friends and family?{" "}
                  <strong>A: {review.q5 || "N/A"}</strong>
                </p>

                <div className="review-textbox">
                  <strong className="customer-review-label">Customer Review</strong>

                  {/* Collapsed or expanded block */}
                  <div
                    className="review-text-block"
                    style={
                      isExpanded
                        ? { maxHeight: "none", overflow: "visible", whiteSpace: "pre-wrap" }
                        : {
                            maxHeight: `${PREVIEW_MAX_HEIGHT}em`,
                            overflow: "hidden",
                            whiteSpace: "pre-wrap",
                          }
                    }
                  >
                    {fullText || "No review text provided."}
                  </div>

                  {/* Toggle */}
                  {needsToggle && (
                    <div style={{ textAlign: "right", marginTop: "8px" }}>
                      <button
                        className="readmore-btn"
                        onClick={() => toggleExpand(review.id)}
                      >
                        {isExpanded ? "Show Less" : "Read More"}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

export default HomePage;

